import s from "assets/scss/FooterMain.module.scss"
import React, { FormEvent, useEffect, useRef, useState } from "react"

import { Link } from "react-router-dom"
import axios from "axios"

import { ReactComponent as ScrollToTop } from "assets/img/scroll-to-top.svg"
import { SmoothContext } from "hocs/WithSmooth"
import { useWindowSize } from "hooks"
import { breakpoints } from "variables"

import api from "api"
import subscribeGif from "assets/img/subscribe.gif"
import Copyright from "components/Copyright"
import IconArrowSquare from "components/Icons/IconArrowSquare"
import { useModalStore } from "store/modalStore"

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

type TurnstileRenderOptions = {
  sitekey: string
  theme?: "light" | "dark"
  size?: "invisible" | "normal"
  callback?: (token: string) => void
  "error-callback"?: () => void
  "expired-callback"?: () => void
}

type TurnstileApi = {
  render: (element: HTMLElement, options: TurnstileRenderOptions) => string
  execute: (widgetId?: string) => void
  reset: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const FooterMain = () => {
  const windowSize = useWindowSize()
  const sContext = React.useContext(SmoothContext)
  const subscribeFormRef = useRef<HTMLFormElement>(null)

  const [email, setEmail] = useState<string | null>(null)
  const [radioChecked, setRadioChecked] = useState(false)
  const [errMessage, setErrMessage] = useState<string | null>(null)
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [pendingTurnstileSubmit, setPendingTurnstileSubmit] = useState(false)
  const [isTurnstileActive, setIsTurnstileActive] = useState(false)
  const turnstileMountRef = useRef<HTMLDivElement>(null)
  const turnstileSuccessRef = useRef<(token: string) => void>(() => {})
  const turnstileErrorRef = useRef<() => void>(() => {})
  const turnstileExpiredRef = useRef<() => void>(() => {})

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITEKEY

  const modalStore = useModalStore()

  const subscribeSuccessModalContent = (
    <div className={s.subscribeSuccess}>
      <img className={s.welcomeAboard} src={subscribeGif} alt='Hand With Mail' />
      <h5 className={s.title}>Welcome aboard!</h5>
      <p className={s.text}>We'll keep in touch with news from the future...</p>
      <button className={s.doneButton} type='button' onClick={() => modalStore.toggle()}>
        DONE
      </button>
    </div>
  )

  const resetTurnstileChallenge = () => {
    setTurnstileToken(null)
    if (!window.turnstile) {
      return
    }

    window.turnstile.reset(turnstileWidgetId ?? undefined)
  }

  const subscribe = async (token?: string) => {
    if (!email) {
      console.log(`Required input email!`)
      return
    }

    const activeToken = token ?? turnstileToken
    if (!activeToken) {
      return
    }

    try {
      // 👇️ const data: CreateUserResponse
      const { data } = await api.post<any>(
        "/home/subscribe.php",
        { email, turnstileToken: activeToken },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      return data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log("error message: ", error.message)
        // 👇️ error: AxiosError<any, any>
        return error.message
      } else {
        console.log("unexpected error: ", error)
        return "An unexpected error occurred"
      }
    }
  }

  const handleSubscriptionResult = (res?: { message: string; success: boolean } | string) => {
    if (typeof res === "string") {
      setErrMessage(res)
      resetTurnstileChallenge()
      return
    }

    if (!res) {
      setErrMessage("Something went wrong. Please try again.")
      resetTurnstileChallenge()
      return
    }

    if (res.success) {
      modalStore.setContent(subscribeSuccessModalContent)
      modalStore.toggle()
      resetForm()
    } else {
      setErrMessage(res.message)
      console.log("A server side error occured!")
      resetTurnstileChallenge()
    }
  }

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token)
    if (!pendingTurnstileSubmit) {
      return
    }

    setPendingTurnstileSubmit(false)
    subscribe(token).then((res) => {
      handleSubscriptionResult(res)
    })
  }

  const handleTurnstileError = () => {
    if (pendingTurnstileSubmit) {
      setErrMessage("Security verification failed. Please try again.")
    }
    setPendingTurnstileSubmit(false)
  }

  const handleTurnstileExpired = () => {
    setTurnstileToken(null)
  }

  turnstileSuccessRef.current = handleTurnstileSuccess
  turnstileErrorRef.current = handleTurnstileError
  turnstileExpiredRef.current = handleTurnstileExpired

  useEffect(() => {
    if (!isTurnstileActive || !turnstileSiteKey || turnstileWidgetId) {
      return
    }

    const renderWidget = () => {
      if (!turnstileMountRef.current || !window.turnstile || turnstileWidgetId) {
        return
      }

      const widgetId = window.turnstile.render(turnstileMountRef.current, {
        sitekey: turnstileSiteKey,
        size: "invisible",
        theme: "dark",
        callback: (token) => turnstileSuccessRef.current(token),
        "error-callback": () => turnstileErrorRef.current(),
        "expired-callback": () => turnstileExpiredRef.current(),
      })

      setTurnstileWidgetId(widgetId)

      if (pendingTurnstileSubmit) {
        window.turnstile.execute(widgetId)
      }
    }

    if (window.turnstile) {
      renderWidget()
      return
    }

    const handleScriptError = () => {
      setErrMessage("Unable to load security verification at the moment.")
    }

    const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`)
    if (existingScript) {
      existingScript.addEventListener("load", renderWidget)
      return () => existingScript.removeEventListener("load", renderWidget)
    }

    const script = document.createElement("script")
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true
    script.addEventListener("load", renderWidget)
    script.addEventListener("error", handleScriptError)
    document.body.appendChild(script)

    return () => {
      script.removeEventListener("load", renderWidget)
      script.removeEventListener("error", handleScriptError)
    }
  }, [turnstileSiteKey, turnstileWidgetId, isTurnstileActive, pendingTurnstileSubmit])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email) {
      return
    }

    setErrMessage(null)

    if (!turnstileSiteKey) {
      setErrMessage("Security verification is not configured yet.")
      return
    }

    if (!isTurnstileActive) {
      setIsTurnstileActive(true)
      setPendingTurnstileSubmit(true)
      return
    }

    if (turnstileToken) {
      subscribe().then((res) => {
        handleSubscriptionResult(res)
      })
      return
    }

    if (turnstileWidgetId && window.turnstile) {
      setPendingTurnstileSubmit(true)
      window.turnstile.execute(turnstileWidgetId)
      return
    }

    // If widget is still initializing
    setPendingTurnstileSubmit(true)
  }

  const resetForm = () => {
    if (subscribeFormRef.current) subscribeFormRef.current.reset()
    setEmail(null)
    setRadioChecked(false)
    setErrMessage(null)
    resetTurnstileChallenge()
  }

  return (
    <div className={s.revealWrapper} data-reveal>
      <div className={s.overlay} data-overlay></div>
      <footer className={s.footerMain}>
        <div className={s.subscribe}>
          <form className={s.formWrapper} ref={subscribeFormRef} onSubmit={(e) => handleSubmit(e)}>
            <div className={s.inputGroup}>
              <h6 className={s.inputLabel}>
                SUBSCRIBE TO
                <br /> OUR EMAIL
              </h6>
              <input
                className={s.input}
                name='email'
                type='email'
                placeholder='YOUR EMAIL ADDRESS'
                required
                onChange={(e) => {
                  setEmail(e.currentTarget.value)
                }}
                onFocus={() => setIsTurnstileActive(true)}
              />
              <div ref={turnstileMountRef} className={s.turnstileWrapper} aria-hidden='true' />
              {errMessage && (
                <div className={s.subscribeError}>
                  <p className={s.errText}>{errMessage}</p>
                </div>
              )}
            </div>
            <div className={s.privacyCheck}>
              <>
                <div
                  className={s.radioWrapper}
                  onClick={() => {
                    setRadioChecked((prev) => !prev)
                  }}
                >
                  <div className={s.customRadio}>
                    <div
                      className={s.inner}
                      style={{
                        transition: "0.2s opacity ease",
                        opacity: radioChecked ? "1" : "0",
                      }}
                    ></div>
                  </div>
                  <p className={s.radioText}>
                    Join our design community to be a part of the future…! <br /> By subscribing, you consent to receive
                    emails from us.
                  </p>
                </div>
                <button
                  className={s.sendBtn}
                  type='submit'
                  style={{
                    transition: "0.2s opacity ease",
                    opacity: radioChecked ? "1" : "0.2",
                    pointerEvents: radioChecked ? "auto" : "none",
                  }}
                >
                  SEND
                </button>
              </>
            </div>
          </form>
          {windowSize.width > breakpoints.tabletMini && (
            <ScrollToTop onClick={sContext.scrollToTop} className={s.scrollToTop} />
          )}
        </div>

        <div className={s.contact}>
          <div className={s.col}>
            <h5 className={s.sayHi}>SAY HI ! 👋</h5>
            <p className={s.text}>
              Start a conversation about new business opportunities,
              <br /> media inquiries or learn about our career options.
            </p>
            <div className={s.buttons}>
              <Link className={s.btn} to='/contact' state={{ contactReason: "NEW_PROJECT" }}>
                <p className={s.btnText}>NEW PROJECT</p>
              </Link>
              <Link className={s.btn} to='/contact' state={{ contactReason: "MEDIA_INQUIRY" }}>
                <p className={s.btnText}>MEDIA INQUIRY</p>
              </Link>
              <a className={s.btn} href='mailto:career@justdesignfx.com'>
                <p className={s.btnText}>CAREER</p>
              </a>
            </div>
          </div>

          <div className={s.col}>
            <h5 className={s.addressTitle}>ISTANBUL HQ</h5>
            <p className={s.textSm}>
              JUSTWork Office Campus Balkan Cad. <br />
              No:62, 34770, ISTANBUL / TURKEY
            </p>
            <div className={s.buttons}>
              <a
                className={s.btnGetDirection}
                target='_blank'
                rel='noreferrer'
                href='https://www.google.com/maps/place/JUSTWork/@41.0234497,29.1265974,15z/data=!4m5!3m4!1s0x0:0x5af99b7e2004cb13!8m2!3d41.0234497!4d29.1265974'
              >
                <p className={s.btnText}>GET DIRECTION</p>
                <div className={s.arrowW}>
                  <IconArrowSquare></IconArrowSquare>
                </div>
              </a>
            </div>
          </div>

          <div className={s.col}>
            <h5 className={s.addressTitle}>NEW YORK HQ</h5>
            <p className={s.textSm}>
              44 W 47th St #22, New York, <br />
              NY 10036 USA
            </p>
            <div className={s.buttons}>
              <a
                className={s.btnGetDirection}
                target='_blank'
                rel='noreferrer'
                href='https://www.google.com/maps/place/44+W+47th+St+%2322,+New+York,+NY+10036,+USA/@40.757209,-73.9807804,17z/data=!3m1!4b1!4m5!3m4!1s0x89c258ffa808b8d3:0x4aa58f11c530b90a!8m2!3d40.757209!4d-73.9807804'
              >
                <p className={s.btnText}>GET DIRECTION</p>
                <div className={s.arrowW}>
                  <IconArrowSquare></IconArrowSquare>
                </div>
              </a>
            </div>
          </div>
        </div>

        <Copyright />
      </footer>
    </div>
  )
}

export default FooterMain
